import { useLazyQuery } from '@apollo/client';
import {
  IRenderFunction,
  ITextFieldProps,
  ITextFieldStyles,
  Link,
  Spinner,
  Stack,
  Text,
  TextField,
  makeStyles,
  useTheme,
} from '@fluentui/react';
import {
  LookupAccountTransactionSearch,
  LookupAccountTransactionSearchVariables,
  LookupAccountTransactionSearch_lookupAccountTransactionSearch_nodes,
} from 'common/graphql/__generated__/LookupAccountTransactionSearch';
import { useCommonStyles } from 'common/styles';
import { RequiredNameProps } from 'common/types/utility';
import { debounce } from 'lodash';
import { FC, useCallback, useState } from 'react';
import { useController } from 'react-hook-form';
import { LookUpCallout } from './Lookup';
import LOOKUP from 'common/graphql/LookupAccountTransactionSearch.graphql';

type LookUpProps =
  LookupAccountTransactionSearch_lookupAccountTransactionSearch_nodes;
type ReimburseAccountFieldProps = RequiredNameProps<ITextFieldProps> & {
  width?: number;
  onVendorSelect?: (lookupData: LookUpProps) => void;
  businessUnitId: string | null;
};

const useStyles = makeStyles((theme) => ({
  resultItem: {
    width: 500,
    backgroundColor: theme.palette.neutralLighter,
    textDecoration: 'none',
    margin: '0px -20px',
    padding: '6px 20px',
    ':hover': {
      backgroundColor: theme.palette.neutralLighter,
    },
    ':active': {
      backgroundColor: theme.palette.neutralLighter,
    },
  },
}));

export const ReimburseAccountField: FC<ReimburseAccountFieldProps> = ({
  width,
  onVendorSelect,
  businessUnitId,
  ...props
}) => {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ name: props.name });
  const theme = useTheme();
  const styles = useStyles();
  const textfieldStyles: Partial<ITextFieldStyles> = {
    suffix: {
      backgroundColor: theme.palette.white,
    },
    fieldGroup: {
      // maxWidth: width,
    },
  };

  const [calloutVisible, setCalloutVisible] = useState(false);
  const [fetchLookUp, { data, loading }] = useLazyQuery<
    LookupAccountTransactionSearch,
    LookupAccountTransactionSearchVariables
  >(LOOKUP, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });
  const { id } = { ...props };

  const callbackLookUp = useCallback(
    debounce((value, queryCalled) => {
      fetchLookUp({
        variables: {
          lookupName: value,
          businessUnitId: businessUnitId,
        },
      });
      queryCalled();
    }, 350),
    [value]
  );

  const commonStyles = useCommonStyles();

  const onRenderSuffix: IRenderFunction<ITextFieldProps> = (props) => {
    if (loading) return <Spinner />;
    else return null;
  };

  const TextfieldProps: ITextFieldProps | undefined = loading
    ? {
        onRenderSuffix,
      }
    : undefined;
  return (
    <Stack>
      <TextField
        // {...rest}
        {...props}
        autoComplete={'off'}
        errorMessage={error?.message}
        value={value || ''}
        onChange={(_event, value) => {
          if (value) {
            onChange(value);

            callbackLookUp(value, () => {
              if (!calloutVisible) setCalloutVisible(true);
            });
          } else {
            onChange(null);
            setCalloutVisible(false);
          }
        }}
        // onRenderSuffix={renderFooter}
        {...TextfieldProps}
        styles={textfieldStyles}
      />

      {calloutVisible &&
        !loading &&
        data?.lookupAccountTransactionSearch?.nodes.length! > 0 && (
          <LookUpCallout
            lookUpCalloutWidth={500}
            onDismiss={() => setCalloutVisible(false)}
            targetID={`#${id!}`}
          >
            {data?.lookupAccountTransactionSearch?.nodes.map((data, index) => {
              return (
                <Link
                  onClick={() => {
                    onChange(data.lookupAccount);
                    setCalloutVisible(false);
                    onVendorSelect?.(data);
                  }}
                  className={styles.resultItem}
                  key={index.toString()}
                >
                  <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Text className={commonStyles.colorThemePrimary}>
                      {data.lookupAccount}
                    </Text>
                    <Text nowrap>{data.lookupName}</Text>
                  </Stack>
                </Link>
              );
            })}
          </LookUpCallout>
        )}
    </Stack>
  );
};
